/**
 * RotaManagementModule - Church service rota management
 * Features: Monthly rota creation, minister assignments, auto-generation, calendar/table views
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Calendar,
  Plus,
  Edit2,
  Trash2,
  Users,
  RefreshCw,
  Save,
  X,
  ChevronDown,
  ChevronRight,
  Loader2,
  Send,
  Download,
  Wand2,
  CheckCircle2,
  AlertCircle,
  Clock,
  BookOpen,
  User,
  UserPlus,
  Table,
  CalendarDays,
  Printer,
  Mail,
  FileText,
  Search
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const SERVICE_COLORS = {
  sunday: 'bg-blue-500',
  sunday_evening: 'bg-indigo-500',
  wednesday: 'bg-purple-500',
  friday: 'bg-emerald-500',
  first_thursday: 'bg-amber-500',
  holy_monday: 'bg-indigo-600',
  holy_tuesday: 'bg-indigo-600',
  holy_wednesday: 'bg-indigo-600',
  holy_thursday: 'bg-indigo-600',
  holy_thursday_6pm: 'bg-indigo-600',
  holy_thursday_feet: 'bg-indigo-700',
  holy_thursday_supper: 'bg-indigo-700',
  good_friday_9am: 'bg-red-700',
  good_friday_12pm: 'bg-red-700',
  good_friday_3pm: 'bg-red-700',
  holy_saturday: 'bg-indigo-600',
  holy_saturday_6pm: 'bg-indigo-600',
  holy_saturday_10pm: 'bg-indigo-600',
  easter_sunday_morning: 'bg-amber-600',
  easter_sunday_evening: 'bg-amber-600',
};

const RotaManagementModule = ({ adminUser }) => {
  const [rotas, setRotas] = useState([]);
  const [currentRota, setCurrentRota] = useState(null);
  const [ministers, setMinisters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table'); // 'table', 'calendar'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMinisterModal, setShowMinisterModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [refreshingLessons, setRefreshingLessons] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  
  const [newRotaData, setNewRotaData] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [rotasRes, ministersRes] = await Promise.all([
        axios.get(`${API}/rota`),
        axios.get(`${API}/ministers?active_only=true`)
      ]);
      setRotas(rotasRes.data);
      setMinisters(ministersRes.data);
      
      // Load current month's rota if exists
      const now = new Date();
      const currentMonthRota = rotasRes.data.find(
        r => r.month === now.getMonth() + 1 && r.year === now.getFullYear()
      );
      if (currentMonthRota) {
        await loadRotaDetails(currentMonthRota.id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  }, []);

  const loadRotaDetails = async (rotaId) => {
    try {
      const res = await axios.get(`${API}/rota/${rotaId}`);
      setCurrentRota(res.data);
    } catch (error) {
      console.error('Error loading rota details:', error);
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateRota = async () => {
    try {
      const res = await axios.post(`${API}/rota`, newRotaData);
      setRotas([...rotas, res.data]);
      setCurrentRota(res.data);
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating rota:', error);
      alert(error.response?.data?.detail || 'Failed to create rota');
    }
  };

  const handleDeleteRota = async (rotaId) => {
    if (!window.confirm('Are you sure you want to delete this rota and all its services?')) return;
    try {
      await axios.delete(`${API}/rota/${rotaId}`);
      setRotas(rotas.filter(r => r.id !== rotaId));
      if (currentRota?.id === rotaId) {
        setCurrentRota(null);
      }
    } catch (error) {
      console.error('Error deleting rota:', error);
    }
  };

  const handleAutoGenerate = async () => {
    if (!currentRota) return;
    if (ministers.length === 0) {
      alert('Please add ministers first before auto-generating assignments');
      return;
    }
    
    setGenerating(true);
    try {
      await axios.post(`${API}/rota/${currentRota.id}/auto-generate`);
      await loadRotaDetails(currentRota.id);
      alert('Rota assignments generated successfully!');
    } catch (error) {
      console.error('Error generating rota:', error);
      alert(error.response?.data?.detail || 'Failed to generate rota');
    }
    setGenerating(false);
  };

  const handlePublishRota = async () => {
    if (!currentRota) return;
    if (!window.confirm('Publish this rota and send emails to all assigned ministers?')) return;
    
    setPublishing(true);
    try {
      await axios.post(`${API}/rota/${currentRota.id}/publish`);
      await loadRotaDetails(currentRota.id);
      alert('Rota published successfully!');
    } catch (error) {
      console.error('Error publishing rota:', error);
    }
    setPublishing(false);
  };

  const handleRefreshBibleLessons = async () => {
    if (!currentRota) return;
    
    setRefreshingLessons(true);
    try {
      const res = await axios.post(`${API}/rota/${currentRota.id}/refresh-bible-lessons`);
      await loadRotaDetails(currentRota.id);
      alert(`Bible lessons refreshed! ${res.data.lessons_added} passages added.`);
    } catch (error) {
      console.error('Error refreshing Bible lessons:', error);
      alert('Failed to refresh Bible lessons');
    }
    setRefreshingLessons(false);
  };

  const handleDownloadPdf = async () => {
    if (!currentRota) return;
    
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/rota/${currentRota.id}/export/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `church_rota_${currentRota.month}_${currentRota.year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF');
    }
    setDownloadingPdf(false);
  };

  const handleDownloadPdfA3 = async () => {
    if (!currentRota) return;
    
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/rota/${currentRota.id}/export/pdf-a3`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `church_rota_${currentRota.month}_${currentRota.year}_A3.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading A3 PDF:', error);
      alert('Failed to download A3 PDF');
    }
    setDownloadingPdf(false);
  };

  const handleEmailMinisters = async (rotaId) => {
    if (!window.confirm('Send rota PDF and assignments to all ministers with email addresses?')) return;
    
    setSendingEmails(true);
    try {
      const res = await axios.post(`${API}/rota/${rotaId}/email-ministers`);
      const summary = res.data.summary;
      
      let message = `Rota emails sent successfully!\n\n`;
      message += `✅ Emails sent: ${summary.emails_sent}\n`;
      if (summary.no_email > 0) {
        message += `⚠️ Ministers without email: ${summary.no_email}\n`;
      }
      if (summary.failed > 0) {
        message += `❌ Failed to send: ${summary.failed}`;
      }
      
      alert(message);
    } catch (error) {
      console.error('Error sending emails:', error);
      alert('Failed to send emails to ministers');
    }
    setSendingEmails(false);
  };

  const handleUpdateAssignment = async (serviceId, role, data) => {
    try {
      await axios.put(`${API}/rota/service/${serviceId}/assignment/${role}`, data);
      await loadRotaDetails(currentRota.id);
      setEditingAssignment(null);
    } catch (error) {
      console.error('Error updating assignment:', error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            Church Rota Management
          </h2>
          <p className="text-slate-500 mt-1">Manage service assignments and minister schedules</p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowMinisterModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors"
          >
            <Users className="w-4 h-4" />
            Ministers
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
            data-testid="create-rota-btn"
          >
            <Plus className="w-4 h-4" />
            Create Rota
          </button>
        </div>
      </div>

      {/* Rota Selection */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-slate-700">Select Rota:</span>
          {rotas.map((rota) => (
            <button
              key={rota.id}
              onClick={() => loadRotaDetails(rota.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                currentRota?.id === rota.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {MONTHS[rota.month - 1]} {rota.year}
              {rota.status === 'published' && (
                <CheckCircle2 className="w-3 h-3 inline ml-1" />
              )}
            </button>
          ))}
          {rotas.length === 0 && (
            <span className="text-slate-500 text-sm">No rotas created yet</span>
          )}
        </div>
      </div>

      {/* Current Rota View */}
      {currentRota && (
        <div className="space-y-4">
          {/* Rota Actions Bar */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <h3 className="text-lg font-semibold text-slate-800">
                  {currentRota.title}
                </h3>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  currentRota.status === 'published' 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {currentRota.status === 'published' ? 'Published' : 'Draft'}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                {/* View Toggle */}
                <div className="flex bg-slate-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('table')}
                    className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                      viewMode === 'table' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'
                    }`}
                    title="Table View"
                  >
                    <Table className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('calendar')}
                    className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                      viewMode === 'calendar' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'
                    }`}
                    title="Calendar View"
                  >
                    <CalendarDays className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('print')}
                    className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                      viewMode === 'print' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'
                    }`}
                    title="Printable View"
                  >
                    <Printer className="w-4 h-4" />
                  </button>
                </div>
                
                <button
                  onClick={handleRefreshBibleLessons}
                  disabled={refreshingLessons}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-xl hover:bg-amber-200 transition-colors disabled:opacity-50"
                  title="Refresh Bible passages from scraped data"
                  data-testid="refresh-bible-lessons-btn"
                >
                  {refreshingLessons ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
                  Refresh Lessons
                </button>
                
                <button
                  onClick={handleAutoGenerate}
                  disabled={generating || ministers.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-xl hover:bg-purple-200 transition-colors disabled:opacity-50"
                  title={ministers.length === 0 ? 'Add ministers first' : 'Auto-generate fair assignments'}
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                  Auto Generate
                </button>
                
                <button
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 transition-colors disabled:opacity-50"
                  title="Download rota as PDF (A4 Landscape)"
                  data-testid="download-pdf-btn"
                >
                  {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  PDF
                </button>
                
                <button
                  onClick={handleDownloadPdfA3}
                  disabled={downloadingPdf}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-xl hover:bg-amber-200 transition-colors disabled:opacity-50"
                  title="Download rota as A3 Portrait (Cards)"
                  data-testid="download-pdf-a3-btn"
                >
                  {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  A3 PDF
                </button>
                
                {currentRota.status !== 'published' && (
                  <button
                    onClick={handlePublishRota}
                    disabled={publishing}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50"
                  >
                    {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Publish & Email
                  </button>
                )}
                
                {currentRota.status === 'published' && (
                  <button
                    onClick={() => handleEmailMinisters(currentRota.id)}
                    disabled={sendingEmails}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
                    title="Send rota to all ministers with email addresses"
                  >
                    {sendingEmails ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    Email Ministers
                  </button>
                )}

                <button
                  onClick={() => navigator.clipboard.writeText('https://cccvob.com/minister-availability')}
                  className="flex items-center gap-2 px-3 py-2 bg-indigo-50 text-indigo-700 rounded-xl hover:bg-indigo-100 transition-colors text-sm font-medium"
                  title="Copy minister availability link"
                  data-testid="copy-availability-link"
                >
                  <Calendar className="w-4 h-4" />
                  Copy Availability Link
                </button>
                
                <button
                  onClick={() => handleDeleteRota(currentRota.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete Rota"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Services Table View */}
          {viewMode === 'table' && currentRota.services && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Service</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Conductor</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Bible Lesson</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Preacher</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {currentRota.services.map((service) => (
                      <ServiceRow
                        key={service.id}
                        service={service}
                        ministers={ministers}
                        onUpdateAssignment={handleUpdateAssignment}
                        editingAssignment={editingAssignment}
                        setEditingAssignment={setEditingAssignment}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Calendar View */}
          {viewMode === 'calendar' && currentRota.services && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {currentRota.services.map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  ministers={ministers}
                  onUpdateAssignment={handleUpdateAssignment}
                />
              ))}
            </div>
          )}

          {/* Printable View */}
          {viewMode === 'print' && currentRota.services && (
            <PrintableRotaView rota={currentRota} />
          )}
        </div>
      )}

      {/* Create Rota Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <Modal onClose={() => setShowCreateModal(false)} title="Create New Rota">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Month</label>
                  <select
                    value={newRotaData.month}
                    onChange={(e) => setNewRotaData({ ...newRotaData, month: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl"
                  >
                    {MONTHS.map((month, idx) => (
                      <option key={month} value={idx + 1}>{month}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Year</label>
                  <select
                    value={newRotaData.year}
                    onChange={(e) => setNewRotaData({ ...newRotaData, year: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl"
                  >
                    {[2025, 2026, 2027].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <p className="text-sm text-slate-500">
                This will automatically generate all Sundays, Wednesdays, Fridays, and the first Thursday for the selected month.
              </p>
              
              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-xl"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateRota}
                  className="px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
                >
                  Create Rota
                </button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Ministers Modal */}
      <AnimatePresence>
        {showMinisterModal && (
          <MinistersModal 
            onClose={() => setShowMinisterModal(false)} 
            onUpdate={loadData}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Service Row Component for Table View
const ServiceRow = ({ service, ministers, onUpdateAssignment, editingAssignment, setEditingAssignment }) => {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const getAssignment = (role) => {
    return service.assignments?.find(a => a.role === role || a.role === role.replace('lesson_reader', 'lesson_reader_1'));
  };

  const conductor = getAssignment('conductor');
  const lessonReader1 = service.service_type === 'sunday' 
    ? getAssignment('lesson_reader_1') 
    : getAssignment('lesson_reader');
  const lessonReader2 = service.service_type === 'sunday' ? getAssignment('lesson_reader_2') : null;
  const preacher = getAssignment('preacher');

  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-3">
        <span className="font-medium text-slate-800">{formatDate(service.date)}</span>
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex px-2 py-1 rounded-lg text-xs font-medium text-white ${SERVICE_COLORS[service.service_type]}`}>
          {service.service_display}
        </span>
      </td>
      <td className="px-4 py-3">
        <AssignmentCell
          assignment={conductor}
          ministers={ministers}
          serviceId={service.id}
          role="conductor"
          onUpdate={onUpdateAssignment}
          serviceDate={service.date}
        />
      </td>
      <td className="px-4 py-3">
        <div className="space-y-1">
          <AssignmentCell
            assignment={lessonReader1}
            ministers={ministers}
            serviceId={service.id}
            role={service.service_type === 'sunday' ? 'lesson_reader_1' : 'lesson_reader'}
            onUpdate={onUpdateAssignment}
            showPassage
            serviceDate={service.date}
          />
          {lessonReader2 && (
            <AssignmentCell
              assignment={lessonReader2}
              ministers={ministers}
              serviceId={service.id}
              role="lesson_reader_2"
              onUpdate={onUpdateAssignment}
              showPassage
              label="2nd"
              serviceDate={service.date}
            />
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <AssignmentCell
          assignment={preacher}
          ministers={ministers}
          serviceId={service.id}
          role="preacher"
          onUpdate={onUpdateAssignment}
          serviceDate={service.date}
        />
      </td>
    </tr>
  );
};

// Assignment Cell Component
const AssignmentCell = ({ assignment, ministers, serviceId, role, onUpdate, showPassage, label, serviceDate }) => {
  const [editing, setEditing] = useState(false);
  const [isGuestMode, setIsGuestMode] = useState(assignment?.is_guest || false);
  const [data, setData] = useState({
    minister_id: assignment?.minister_id || '',
    minister_name: assignment?.minister_name || '',
    bible_passage: assignment?.bible_passage || '',
    is_guest: assignment?.is_guest || false,
    guest_name: '',
    guest_title: 'Brother'
  });

  // Check if the assigned minister is unavailable on this date
  const assignedMinister = ministers.find(m => m.id === assignment?.minister_id);
  const isUnavailable = assignedMinister?.unavailable_dates?.includes(serviceDate);

  const handleSave = async () => {
    if (isGuestMode && data.guest_name) {
      // Use guest minister endpoint
      try {
        const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;
        await axios.post(`${API}/rota/service/guest-assignment`, {
          service_id: serviceId,
          role: role,
          guest_name: data.guest_name,
          guest_title: data.guest_title,
          bible_passage: data.bible_passage || null
        });
        setEditing(false);
        // Refresh will be handled by parent
        onUpdate(serviceId, role, data);
      } catch (error) {
        console.error('Error assigning guest:', error);
        alert('Failed to assign guest minister');
      }
    } else {
      onUpdate(serviceId, role, data);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div className="space-y-2 min-w-[200px]">
        {/* Toggle: Regular vs Guest */}
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg text-xs">
          <button
            onClick={() => setIsGuestMode(false)}
            className={`flex-1 py-1.5 px-2 rounded-md transition-colors ${
              !isGuestMode ? 'bg-white shadow text-blue-600 font-medium' : 'text-slate-500'
            }`}
          >
            <User className="w-3 h-3 inline mr-1" />
            Minister
          </button>
          <button
            onClick={() => setIsGuestMode(true)}
            className={`flex-1 py-1.5 px-2 rounded-md transition-colors ${
              isGuestMode ? 'bg-white shadow text-purple-600 font-medium' : 'text-slate-500'
            }`}
          >
            <UserPlus className="w-3 h-3 inline mr-1" />
            Guest
          </button>
        </div>

        {isGuestMode ? (
          /* Guest Minister Input */
          <>
            <select
              value={data.guest_title}
              onChange={(e) => setData({ ...data, guest_title: e.target.value })}
              className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg"
            >
              <option value="Brother">Brother</option>
              <option value="Bro">Bro</option>
              <option value="Sister">Sister</option>
              <option value="Sis">Sis</option>
              <option value="Elder">Elder</option>
              <option value="Evangelist">Evangelist</option>
              <option value="Prophet">Prophet</option>
              <option value="Prophetess">Prophetess</option>
              <option value="Pastor">Pastor</option>
              <option value="Rev.">Rev.</option>
            </select>
            <input
              type="text"
              value={data.guest_name}
              onChange={(e) => setData({ ...data, guest_name: e.target.value })}
              placeholder="Guest's full name"
              className="w-full px-2 py-1 text-sm border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-300"
              autoFocus
            />
          </>
        ) : (
          /* Regular Minister Select */
          <select
            value={data.minister_id}
            onChange={(e) => {
              const minister = ministers.find(m => m.id === e.target.value);
              setData({
                ...data,
                minister_id: e.target.value,
                minister_name: minister ? `${minister.title} ${minister.name}` : '',
                is_guest: false
              });
            }}
            className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg"
          >
            <option value="">Select Minister</option>
            {ministers.map(m => {
              const mUnavailable = m.unavailable_dates?.includes(serviceDate);
              return (
                <option key={m.id} value={m.id} style={mUnavailable ? { color: '#dc2626' } : {}}>
                  {m.title} {m.name}{mUnavailable ? ' (UNAVAILABLE)' : ''}
                </option>
              );
            })}
          </select>
        )}
        
        {showPassage && (
          <input
            type="text"
            value={data.bible_passage}
            onChange={(e) => setData({ ...data, bible_passage: e.target.value })}
            placeholder="Bible Passage (e.g., Genesis 21:1-21)"
            className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg"
          />
        )}
        
        <div className="flex gap-2">
          <button
            onClick={handleSave}
            className="px-2 py-1 bg-blue-600 text-white text-xs rounded"
          >
            Save
          </button>
          <button
            onClick={() => setEditing(false)}
            className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      onClick={() => setEditing(true)}
      className="cursor-pointer hover:bg-slate-100 rounded p-1 -m-1"
    >
      {label && <span className="text-xs text-slate-400 mr-1">{label}:</span>}
      {assignment?.minister_name ? (
        <div>
          <span className="text-sm text-slate-800">
            {assignment.is_guest && (
              <span className="text-purple-600 text-xs font-medium mr-1">(Guest)</span>
            )}
            {assignment.minister_name}
          </span>
          {isUnavailable && (
            <span className="ml-1.5 px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full" data-testid={`unavailable-warning-${serviceId}-${role}`}>
              UNAVAILABLE
            </span>
          )}
          {showPassage && assignment.bible_passage && (
            <p className="text-xs text-slate-500">{assignment.bible_passage}</p>
          )}
        </div>
      ) : (
        <span className="text-sm text-slate-400 italic">Click to assign</span>
      )}
    </div>
  );
};

// Service Card for Calendar View
const ServiceCard = ({ service, ministers, onUpdateAssignment }) => {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden`}>
      <div className={`px-4 py-3 ${SERVICE_COLORS[service.service_type]}`}>
        <h4 className="font-semibold text-white">{service.service_display}</h4>
        <p className="text-white/80 text-sm">{formatDate(service.date)}</p>
      </div>
      <div className="p-4 space-y-3">
        {service.assignments?.map((assignment) => (
          <div key={assignment.role} className="flex items-start gap-2">
            <span className="text-xs text-slate-500 w-24 flex-shrink-0 pt-0.5">
              {assignment.role_display}:
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-800">
                {assignment.minister_name || <span className="text-slate-400 italic">Not assigned</span>}
              </p>
              {assignment.bible_passage && (
                <p className="text-xs text-slate-500">{assignment.bible_passage}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Printable Rota View Component - LANDSCAPE ONE PAGE - FORCED FIT
const PrintableRotaView = ({ rota }) => {
  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
  };

  const getServiceTypeColor = (type) => {
    const colors = {
      sunday: '#3b82f6',
      wednesday: '#8b5cf6',
      friday: '#10b981',
      first_thursday: '#f59e0b',
      saturday: '#ef4444',
      holy_monday: '#4f46e5',
      holy_tuesday: '#4f46e5',
      holy_wednesday: '#4f46e5',
      holy_thursday: '#4f46e5',
      holy_thursday_feet: '#4338ca',
      good_friday_9am: '#b91c1c',
      good_friday_12pm: '#b91c1c',
      good_friday_3pm: '#b91c1c',
      holy_saturday: '#4f46e5'
    };
    return colors[type] || '#64748b';
  };

  const getServiceShortName = (type) => {
    const names = {
      sunday: 'Sun',
      wednesday: 'Wed',
      friday: 'Fri',
      first_thursday: 'Thu',
      saturday: 'Sat',
      holy_monday: 'H.Mon',
      holy_tuesday: 'H.Tue',
      holy_wednesday: 'H.Wed',
      holy_thursday: 'H.Thu',
      holy_thursday_feet: 'H.Thu(F)',
      good_friday_9am: 'G.Fri 9am',
      good_friday_12pm: 'G.Fri 12pm',
      good_friday_3pm: 'G.Fri 3pm',
      holy_saturday: 'H.Sat'
    };
    return names[type] || type?.slice(0, 3);
  };

  const sortedServices = rota.services?.sort((a, b) => a.date.localeCompare(b.date)) || [];

  return (
    <div className="space-y-4">
      {/* Print Button */}
      <div className="flex justify-end no-print gap-2">
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-xl hover:bg-slate-700"
        >
          <Printer className="w-4 h-4" />
          Print Rota (Landscape)
        </button>
      </div>

      {/* Printable Content */}
      <div id="printable-rota" className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 print-area">
        {/* Header */}
        <div className="text-center mb-3 border-b-2 border-amber-400 pb-2">
          <div className="flex items-center justify-center gap-3">
            <img src="/church-logo.png?v=2" alt="Logo" className="w-12 h-12 object-contain" onError={(e) => e.target.style.display = 'none'} />
            <div>
              <h1 className="text-lg font-bold text-slate-800">CELESTIAL CHURCH OF CHRIST</h1>
              <p className="text-xs text-slate-500">Valley of Berachah Parish</p>
            </div>
          </div>
          <h2 className="text-base font-semibold text-blue-600 mt-2">
            {rota.title || `Church Rota - ${MONTHS[rota.month - 1]} ${rota.year}`}
          </h2>
        </div>

        {/* Rota Table */}
        <table className="w-full border-collapse rota-print-table">
          <thead>
            <tr className="bg-blue-700 text-white">
              <th className="border border-slate-300 px-2 py-1.5 text-left text-xs font-semibold">Date</th>
              <th className="border border-slate-300 px-2 py-1.5 text-left text-xs font-semibold">Svc</th>
              <th className="border border-slate-300 px-2 py-1.5 text-left text-xs font-semibold">Conductor</th>
              <th className="border border-slate-300 px-2 py-1.5 text-left text-xs font-semibold">1st Lesson</th>
              <th className="border border-slate-300 px-2 py-1.5 text-left text-xs font-semibold">Passage</th>
              <th className="border border-slate-300 px-2 py-1.5 text-left text-xs font-semibold">2nd Lesson</th>
              <th className="border border-slate-300 px-2 py-1.5 text-left text-xs font-semibold">Passage</th>
              <th className="border border-slate-300 px-2 py-1.5 text-left text-xs font-semibold">Preacher</th>
            </tr>
          </thead>
          <tbody>
            {sortedServices.map((service, idx) => {
              const conductor = service.assignments?.find(a => a.role === 'conductor');
              const lesson1 = service.assignments?.find(a => a.role === 'lesson_reader_1' || a.role === 'lesson_reader');
              const lesson2 = service.assignments?.find(a => a.role === 'lesson_reader_2');
              const preacher = service.assignments?.find(a => a.role === 'preacher');

              return (
                <tr key={service.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="border border-slate-300 px-2 py-1.5 text-xs font-medium whitespace-nowrap">
                    {formatDate(service.date)}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-xs font-bold" style={{ color: getServiceTypeColor(service.service_type) }}>
                    {getServiceShortName(service.service_type)}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-xs">
                    {conductor?.minister_name || '-'}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-xs">
                    {lesson1?.minister_name || '-'}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-xs text-slate-600">
                    {lesson1?.bible_passage || '-'}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-xs">
                    {lesson2?.minister_name || '-'}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-xs text-slate-600">
                    {lesson2?.bible_passage || '-'}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-xs">
                    {preacher?.minister_name || '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Footer */}
        <div className="mt-3 pt-2 border-t border-slate-200 text-center">
          <p className="text-xs text-slate-400">
            Generated: {new Date().toLocaleDateString('en-GB')} | CCC Valley of Berachah Parish | 62 Spital Street, Dartford DA1 2DT
          </p>
        </div>
      </div>

      {/* CRITICAL: Force Landscape One Page Print */}
      <style>{`
        @media print {
          @page {
            size: landscape;
            margin: 10mm;
          }
          
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          html, body {
            width: 100% !important;
            height: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
          }
          
          body * {
            visibility: hidden;
          }
          
          .print-area, .print-area * {
            visibility: visible;
          }
          
          .print-area {
            position: fixed !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            height: auto !important;
            max-height: 100vh !important;
            padding: 5mm !important;
            margin: 0 !important;
            box-shadow: none !important;
            border: none !important;
            border-radius: 0 !important;
            background: white !important;
            page-break-inside: avoid !important;
            page-break-after: avoid !important;
            page-break-before: avoid !important;
          }
          
          .no-print {
            display: none !important;
          }
          
          .rota-print-table {
            width: 100% !important;
            border-collapse: collapse !important;
            table-layout: fixed !important;
            page-break-inside: avoid !important;
          }
          
          .rota-print-table th {
            background: #1e40af !important;
            color: white !important;
            font-size: 8pt !important;
            padding: 4px 6px !important;
            font-weight: bold !important;
          }
          
          .rota-print-table td {
            font-size: 8pt !important;
            padding: 3px 5px !important;
            border: 0.5pt solid #cbd5e1 !important;
          }
          
          .rota-print-table tbody tr:nth-child(even) {
            background: #f1f5f9 !important;
          }
          
          h1 { font-size: 14pt !important; }
          h2 { font-size: 11pt !important; }
          p { font-size: 7pt !important; }
          
          img { max-height: 12mm !important; width: auto !important; }
        }
      `}</style>
    </div>
  );
};

// Modal Component
const Modal = ({ children, onClose, title }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
    onClick={onClose}
  >
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
      onClick={(e) => e.stopPropagation()}
      className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden"
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
        <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
        <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
          <X className="w-5 h-5 text-slate-500" />
        </button>
      </div>
      <div className="p-6">{children}</div>
    </motion.div>
  </motion.div>
);

// Service-specific roles
const SERVICE_ROLES = {
  sunday: [
    { key: 'sunday_conductor', label: 'Conductor' },
    { key: 'sunday_lesson_reader_1', label: '1st Lesson Reader' },
    { key: 'sunday_lesson_reader_2', label: '2nd Lesson Reader' },
    { key: 'sunday_preacher', label: 'Preacher' }
  ],
  wednesday_friday: [
    { key: 'wednesday_friday_conductor', label: 'Conductor' },
    { key: 'wednesday_friday_lesson_reader', label: 'Lesson Reader' },
    { key: 'wednesday_friday_preacher', label: 'Preacher' }
  ],
  thursday: [
    { key: 'thursday_conductor', label: 'Conductor' },
    { key: 'thursday_lesson_reader', label: 'Lesson Reader' },
    { key: 'thursday_preacher', label: 'Preacher' }
  ]
};

// Ministers Modal Component
const MinistersModal = ({ onClose, onUpdate }) => {
  const [ministers, setMinisters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMinister, setEditingMinister] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    email: '',
    phone: '',
    eligible_roles: [],
    available_days: ['sunday', 'wednesday', 'friday', 'thursday']
  });

  // Member search state
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [showMemberSearch, setShowMemberSearch] = useState(false);

  // Filter: show only ministers missing email
  const [showOnlyMissingEmail, setShowOnlyMissingEmail] = useState(false);

  useEffect(() => {
    loadMinisters();
  }, []);

  const loadMinisters = async () => {
    try {
      const res = await axios.get(`${API}/ministers`);
      setMinisters(res.data);
    } catch (error) {
      console.error('Error loading ministers:', error);
    }
    setLoading(false);
  };

  const loadMembers = async () => {
    try {
      const res = await axios.get(`${API}/members`);
      setAllMembers(res.data);
    } catch (error) {
      console.error('Error loading members:', error);
    }
  };

  useEffect(() => {
    if (memberSearch.trim().length >= 1) {
      const q = memberSearch.toLowerCase();
      setMemberResults(allMembers.filter(m =>
        m.full_name?.toLowerCase().includes(q) || m.member_number?.toLowerCase().includes(q)
      ).slice(0, 8));
    } else {
      setMemberResults([]);
    }
  }, [memberSearch, allMembers]);

  const selectMember = (member) => {
    setFormData(prev => ({
      ...prev,
      name: member.full_name || '',
      email: member.email || '',
      phone: member.phone || ''
    }));
    setMemberSearch('');
    setMemberResults([]);
    setShowMemberSearch(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      email: '',
      phone: '',
      eligible_roles: [],
      available_days: ['sunday', 'wednesday', 'friday', 'thursday']
    });
    setEditingMinister(null);
    setShowAddForm(false);
    setMemberSearch('');
    setMemberResults([]);
    setShowMemberSearch(false);
  };

  const handleAddMinister = async () => {
    try {
      await axios.post(`${API}/ministers`, formData);
      await loadMinisters();
      onUpdate();
      resetForm();
    } catch (error) {
      console.error('Error adding minister:', error);
    }
  };

  const handleEditMinister = (minister) => {
    setEditingMinister(minister);
    setFormData({
      name: minister.name || '',
      title: minister.title || '',
      email: minister.email || '',
      phone: minister.phone || '',
      eligible_roles: minister.eligible_roles || [],
      available_days: minister.available_days || ['sunday', 'wednesday', 'friday', 'thursday']
    });
    setShowAddForm(true);
  };

  const handleUpdateMinister = async () => {
    if (!editingMinister) return;
    try {
      await axios.put(`${API}/ministers/${editingMinister.id}`, formData);
      await loadMinisters();
      onUpdate();
      resetForm();
    } catch (error) {
      console.error('Error updating minister:', error);
    }
  };

  const handleDeleteMinister = async (id) => {
    if (!window.confirm('Delete this minister?')) return;
    try {
      await axios.delete(`${API}/ministers/${id}`);
      await loadMinisters();
      onUpdate();
    } catch (error) {
      console.error('Error deleting minister:', error);
    }
  };

  const handleSeedMinisters = async () => {
    try {
      const res = await axios.post(`${API}/ministers/seed`);
      alert(res.data.message);
      await loadMinisters();
      onUpdate();
    } catch (error) {
      console.error('Error seeding ministers:', error);
    }
  };

  const toggleRole = (role) => {
    setFormData(prev => ({
      ...prev,
      eligible_roles: prev.eligible_roles.includes(role)
        ? prev.eligible_roles.filter(r => r !== role)
        : [...prev.eligible_roles, role]
    }));
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      available_days: prev.available_days.includes(day)
        ? prev.available_days.filter(d => d !== day)
        : [...prev.available_days, day]
    }));
  };

  const getRoleDisplayName = (role) => {
    const allRoles = [...SERVICE_ROLES.sunday, ...SERVICE_ROLES.wednesday_friday, ...SERVICE_ROLES.thursday];
    const found = allRoles.find(r => r.key === role);
    if (found) {
      if (role.startsWith('sunday_')) return `Sun: ${found.label}`;
      if (role.startsWith('wednesday_friday_')) return `Wed/Fri: ${found.label}`;
      if (role.startsWith('thursday_')) return `Thu: ${found.label}`;
    }
    return role.replace(/_/g, ' ');
  };

  // Count ministers with/without email
  const ministersWithEmail = ministers.filter(m => m.email).length;
  const ministersWithoutEmail = ministers.length - ministersWithEmail;
  const missingEmailMinisters = ministers.filter(m => !m.email);
  const visibleMinisters = showOnlyMissingEmail ? missingEmailMinisters : ministers;

  const copyMissingEmailNames = async () => {
    if (missingEmailMinisters.length === 0) return;
    const text = missingEmailMinisters
      .map((m, i) => `${i + 1}. ${m.title ? m.title + ' ' : ''}${m.name}`)
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
      alert(`Copied ${missingEmailMinisters.length} names to clipboard:\n\n${text}`);
    } catch {
      window.prompt('Copy the list below:', text);
    }
  };

  return (
    <Modal onClose={onClose} title="Ministers Directory">
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Email Stats */}
          {ministers.length > 0 && (
            <div className="flex flex-wrap gap-3 items-center p-3 bg-slate-50 rounded-xl text-sm">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-green-600" />
                <span className="text-slate-600">With email: <strong className="text-green-600">{ministersWithEmail}</strong></span>
              </div>
              <button
                type="button"
                onClick={() => setShowOnlyMissingEmail(v => !v)}
                data-testid="toggle-missing-email-filter"
                className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  showOnlyMissingEmail
                    ? 'bg-amber-500 text-white'
                    : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                }`}
                title="Click to filter ministers missing email"
              >
                <AlertCircle className="w-3.5 h-3.5" />
                No email: <strong>{ministersWithoutEmail}</strong>
                {showOnlyMissingEmail && <span className="ml-1">· Showing</span>}
              </button>
              {ministersWithoutEmail > 0 && (
                <button
                  type="button"
                  onClick={copyMissingEmailNames}
                  data-testid="copy-missing-email-names"
                  className="px-3 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-full text-xs font-medium"
                  title="Copy the list of ministers missing email to clipboard"
                >
                  Copy names
                </button>
              )}
            </div>
          )}
          
          {/* Add/Edit Form */}
          {showAddForm ? (
            <div className="space-y-4 p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-slate-800">
                  {editingMinister ? 'Edit Minister' : 'Add New Minister'}
                </h3>
              </div>

              {/* Member Search — only for new ministers */}
              {!editingMinister && (
                <div className="relative">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Search from Church Members
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={memberSearch}
                      onChange={e => setMemberSearch(e.target.value)}
                      onFocus={() => { setShowMemberSearch(true); if (allMembers.length === 0) loadMembers(); }}
                      placeholder="Type member name or number..."
                      data-testid="minister-member-search"
                      className="w-full pl-10 pr-4 py-2 border border-blue-200 rounded-lg text-sm bg-blue-50 focus:bg-white focus:border-blue-400 outline-none"
                    />
                  </div>
                  {showMemberSearch && memberResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto"
                      data-testid="member-search-results">
                      {memberResults.map(m => (
                        <button key={m.id} onClick={() => selectMember(m)}
                          data-testid={`select-member-${m.id}`}
                          className="w-full text-left px-4 py-2.5 hover:bg-blue-50 flex items-center justify-between border-b border-slate-100 last:border-0">
                          <div>
                            <p className="font-medium text-sm text-slate-800">{m.full_name}</p>
                            <p className="text-xs text-slate-400">{m.phone || 'No phone'} {m.email ? `· ${m.email}` : ''}</p>
                          </div>
                          <span className="text-xs font-mono text-blue-500 bg-blue-50 px-2 py-0.5 rounded">{m.member_number}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {showMemberSearch && memberSearch.trim().length >= 1 && memberResults.length === 0 && (
                    <p className="text-xs text-slate-400 mt-1">No members found. You can type details manually below.</p>
                  )}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Daniel Bodunde"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Brother, Sister, Evangelist, Prophet"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Email (for rota notifications)</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@example.com"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+44 7000 000000"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
              </div>
              
              {/* Sunday Roles */}
              <div>
                <p className="text-xs font-semibold text-blue-600 mb-2 flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                  Sunday Service Roles
                </p>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_ROLES.sunday.map(role => (
                    <button
                      key={role.key}
                      onClick={() => toggleRole(role.key)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        formData.eligible_roles.includes(role.key)
                          ? 'bg-blue-600 text-white'
                          : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      }`}
                    >
                      {role.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Wednesday/Friday Roles */}
              <div>
                <p className="text-xs font-semibold text-purple-600 mb-2 flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                  Wednesday & Friday Service Roles
                </p>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_ROLES.wednesday_friday.map(role => (
                    <button
                      key={role.key}
                      onClick={() => toggleRole(role.key)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        formData.eligible_roles.includes(role.key)
                          ? 'bg-purple-600 text-white'
                          : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                      }`}
                    >
                      {role.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Thursday Roles */}
              <div>
                <p className="text-xs font-semibold text-amber-600 mb-2 flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                  First Thursday Service Roles
                </p>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_ROLES.thursday.map(role => (
                    <button
                      key={role.key}
                      onClick={() => toggleRole(role.key)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        formData.eligible_roles.includes(role.key)
                          ? 'bg-amber-600 text-white'
                          : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                      }`}
                    >
                      {role.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Available Days */}
              <div>
                <p className="text-xs font-medium text-slate-600 mb-2">Available Days:</p>
                <div className="flex flex-wrap gap-2">
                  {['sunday', 'wednesday', 'friday', 'thursday'].map(day => (
                    <button
                      key={day}
                      onClick={() => toggleDay(day)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                        formData.available_days.includes(day)
                          ? 'bg-emerald-600 text-white'
                          : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-2 pt-2 border-t border-slate-200">
                <button
                  onClick={editingMinister ? handleUpdateMinister : handleAddMinister}
                  disabled={!formData.name || !formData.title || formData.eligible_roles.length === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  {editingMinister ? 'Update Minister' : 'Add Minister'}
                </button>
                <button
                  onClick={resetForm}
                  className="px-4 py-2 bg-slate-200 text-slate-600 rounded-lg text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
              >
                <UserPlus className="w-4 h-4" />
                Add Minister
              </button>
              {ministers.length === 0 && (
                <button
                  onClick={handleSeedMinisters}
                  className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm"
                >
                  Load Sample Ministers
                </button>
              )}
            </div>
          )}

          {/* Ministers List */}
          <div className="space-y-2">
            {showOnlyMissingEmail && visibleMinisters.length === 0 && (
              <p className="text-center text-sm text-slate-500 py-6">
                All ministers have email addresses.
              </p>
            )}
            {visibleMinisters.map((minister) => (
              <div
                key={minister.id}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-800">
                      {minister.title} {minister.name}
                    </p>
                    {minister.email ? (
                      <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                        <Mail className="w-3 h-3" />
                        Email
                      </span>
                    ) : (
                      <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                        No email
                      </span>
                    )}
                  </div>
                  {(minister.email || minister.phone) && (
                    <div className="flex gap-4 mt-1 text-xs text-slate-500">
                      {minister.email && <span>{minister.email}</span>}
                      {minister.phone && <span>{minister.phone}</span>}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {minister.eligible_roles?.slice(0, 4).map(role => (
                      <span key={role} className="px-1.5 py-0.5 bg-slate-200 text-slate-600 text-xs rounded">
                        {getRoleDisplayName(role)}
                      </span>
                    ))}
                    {minister.eligible_roles?.length > 4 && (
                      <span className="px-1.5 py-0.5 bg-slate-200 text-slate-600 text-xs rounded">
                        +{minister.eligible_roles.length - 4} more
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEditMinister(minister)}
                    className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                    title="Edit minister"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteMinister(minister.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    title="Delete minister"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default RotaManagementModule;
